import { Filter } from '@vrplatform/components';
import { useInfiniteQuery, useTeam } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { InfiniteTable, type MRT_SortingState } from '@vrplatform/table';
import { Stack, Text } from '@mantine/core';
import type { change_bool_exp } from '@vrplatform/graphql';
import { useMemo, useState } from 'react';
import { useSyncedFromToTableColumns } from '../_hooks';
import { getSubtask, getWorkflowChangeRow } from '../_utils';

export const SyncedToTable = ({
  workflowId,
  type,
  isWorkflowPending,
}: {
  workflowId: string;
  type: 'failed' | 'success';
  isWorkflowPending: boolean;
}) => {
  const [{ id: teamId }] = useTeam();
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [search, setSearch] = useState('');

  const infiniteData = useInfiniteQuery(
    (q, { workflowId, search, type, teamId }, { limit, offset }) => {
      if (!workflowId)
        return {
          aggregate: 0,
          list: [],
        };

      const overwrites =
        offset !== 0
          ? []
          : q
              .issueMessageOverwrites({
                order_by: [{ pattern: 'asc_nulls_last' }],
              })
              .map((o) => ({
                pattern: o.pattern || '',
                message: o.message || '',
              }));

      const subtasks =
        type === 'failed'
          ? q
              .syncSubtasks({
                where: {
                  tenantId: { _eq: teamId },
                  syncId: { _eq: workflowId },
                  status: {
                    _eq: 'failed',
                  },
                },
              })
              .map(getSubtask)
          : [];

      const whereChange: change_bool_exp = {
        tenantId: { _eq: teamId },
        syncId: { _eq: workflowId },
        syncType: type === 'failed' ? undefined : { _eq: 'push' },
        message: search ? { _ilike: `%${search}%` } : undefined,
        status: type === 'failed' ? { _eq: 'failed' } : undefined,
      };

      const aggregate =
        q.changeAggregate({ where: whereChange }).aggregate?.count() || 0;

      const list = q
        .changes({
          where: whereChange,
          limit,
          offset,
          order_by: [{ createdAt: 'desc' }],
        })
        .map((change) => getWorkflowChangeRow(change, 'synced-to'));

      return {
        aggregate: aggregate + subtasks.length,
        list: [...list, ...subtasks],
        overwrites,
      };
    },
    {
      variables: {
        search: search.trim(),
        workflowId,
        sorting,
        type,
        teamId,
      },
      queryKey: ['jobPlans'],
      poll: {
        isCompleted: () => isWorkflowPending === false,
        interval: 5000,
      },
    }
  );

  const overwrites = useMemo(
    () => infiniteData?.data?.pages?.[0]?.overwrites || [],
    [infiniteData?.data?.pages?.[0]?.overwrites]
  );

  const columns = useSyncedFromToTableColumns(overwrites);

  const resetFilter = () => {
    setSearch('');
  };

  return (
    <InfiniteTable
      columns={columns}
      table={{
        hideHeader: true,
        emptyRowsFallback:
          type === 'failed'
            ? () => (
                <Stack mih={200} align="center" justify="center">
                  <Icon
                    icon="CheckCircleIcon"
                    color={(theme) => theme.colors.green[5]}
                    size={30}
                  />
                  <Text c="neutral" ta="center">
                    No errors, all parts are running smoothly.
                  </Text>
                </Stack>
              )
            : 'Nothing was synced here.',
      }}
      queryData={infiniteData}
      sorting={{
        setSorting,
        sorting,
      }}
      resetFilter={resetFilter}
    >
      <Filter.Search value={search} setValue={setSearch} />
    </InfiniteTable>
  );
};
